"use client";

// @ts-ignore
import * as React from "react";
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Calendar, User, DollarSign, Package, History, CheckCircle, FileText, NotebookPen } from "lucide-react";
import { cn } from "@/lib/utils";

// Existing desktop sections - reuse as-is (except we override Payment for mobile)
import { DetailsSection } from "./details-section";
import { StatusSectionMobile } from "./mobile/status-section-mobile";
import { ClientDataSection } from "./client-data-section";
import { ClientNotesSection } from "./client-notes-section";
import { ConsumablesSection } from "./consumables-section";
// Desktop visit-history (table) replaced with mobile card panel
import { VisitHistorySectionMobile } from "./mobile/visit-history-section-mobile";
import { PaymentSectionMobile } from "./mobile/payment-section-mobile";
import { ChangesSectionMobile } from "./mobile/changes-section-mobile";

// Mobile-specific components
import { AccordionTriggerRow } from "./mobile/AccordionTriggerRow";
import {
  getDetailsSummary,
  getStatusSummary,
  getClientSummary,
  getPaymentSummary,
  getConsumablesSummary,
  getVisitHistorySummary,
  getChangesSummary,
} from "./mobile/section-summaries";

// Context and hooks
import { useAppointmentModal } from "@/contexts/AppointmentModalContext";
import { useAppointmentModalDetailsInfo } from "@/hooks/modal/useAppointmentModalDetailsInfo";
import { useEditAppointmentRecord } from "@/hooks/modal/useEditAppointmentRecord";
import { useComputedPayment } from "@/hooks/modal/useComputedPayment";
import { useAccessPolicy } from "@/hooks/access/useAccess";

type SectionKey = "details" | "status" | "client-data" | "client-notes" | "payment" | "consumables" | "visit-history" | "changes";

/**
 * Mobile accordion-based appointment modal.
 * Designed for screens <768px width.
 */
export function AppointmentModalMobile() {
  const {
    open,
    appointmentId,
    appointment,
    setAppointment,
    closeModal,
  } = useAppointmentModal();

  const { data: appointmentData, isLoading } = useAppointmentModalDetailsInfo();
  const { mutate: editAppointment, isPending: isEditing } = useEditAppointmentRecord();
  const computedPayment = useComputedPayment();
  const policy = useAccessPolicy();
  const { t } = useTranslation();
  const paymentMethods = appointment?.paymentSection?.paymentMethods ?? [];
  const paidTotal = paymentMethods.reduce(
    (sum, m: any) => sum + (Number(m?.account?.paid_amount) || 0),
    0
  );
  const positiveCount = paymentMethods.filter((m: any) => Number(m?.account?.paid_amount) > 0).length;
  const totalAmount = computedPayment?.totalAmount ?? 0;
  const isSplitSelection =
    (appointment?.paymentSection as any)?.paymentMode === "split" || positiveCount > 1;
  const isSplitInvalid =
    paymentMethods.length > 0 && isSplitSelection && Math.abs(paidTotal - totalAmount) >= 0.01;

  // Initialize context appointment when data is loaded (mirror desktop behavior)
  useEffect(() => {
    if (appointmentId && appointmentData) {
      setAppointment(appointmentData);
    }
  }, [appointmentId, appointmentData, setAppointment]);

  // Controlled accordion state - single-expand behavior, start collapsed
  const [openValue, setOpenValue] = useState<SectionKey | undefined>(undefined);

  // Header title: Client name + record ID
  const headerTitle = useMemo(() => {
    if (!appointmentData) return t("appointment.header.record", "Appointment");
    const name = appointmentData.client?.name?.trim();
    const id = appointmentData.id ? `#${appointmentData.id}` : "";
    return name ? `${name} ${id}` : `${t("appointment.header.record", "Appointment")} ${id}`;
  }, [appointmentData, t]);

  // Generate summaries for accordion triggers
  const summaries = useMemo(
    () => ({
      details: getDetailsSummary(appointment),
      status: getStatusSummary(appointment),
      client: getClientSummary(appointment, policy),
      payment: getPaymentSummary(computedPayment || undefined),
      consumables: getConsumablesSummary(appointment),
      visitHistory: getVisitHistorySummary(appointment),
      changes: getChangesSummary(),
    }),
    [appointment, policy, computedPayment]
  );

  // Handlers
  const handleDelete = () => {
    if (!appointment) return;
    editAppointment({ ...(appointment as any), deleted: 1 }, { onSuccess: () => closeModal() });
  };

  const handleSave = () => {
    if (!appointment) return;
    if (isSplitInvalid) {
      alert(t("appointment.payment.split_sum_mismatch", "Payment amounts must equal the total"));
      return;
    }
    editAppointment(appointment, {
      onSuccess: () => {
        closeModal();
      },
      onError: (error: any) => {
        // Same as the desktop modal: never fail silently — surface the
        // backend's guiding message (phone collision etc.).
        const message =
          error?.response?.data?.error ??
          error?.response?.data?.detail ??
          t("appointment.modal.save_failed", "Could not save the appointment. Please try again.");
        alert(message);
      },
    });
  };

  const shouldShowContent = open && appointmentId && appointmentData && !isLoading;

  return (
    <Dialog open={open} onOpenChange={closeModal}>
      <DialogContent
        className={cn(
          "w-full max-w-full p-0 gap-0 overflow-hidden",
          "h-[100dvh] rounded-none", // Full dynamic height, no rounded corners
          "flex flex-col",
          "fixed left-0 top-0",
          // Force override default Radix centering transforms to prevent clipping
          "!translate-x-0 !translate-y-0",
          "data-[state=open]:slide-in-from-bottom-0",
          "data-[state=closed]:slide-out-to-bottom-0"
        )}
      >
        {/* Hidden accessibility labels */}
        <DialogTitle className="sr-only">
          {headerTitle}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {t("appointment.description.view_edit_client_record", "View and edit client appointment")}
        </DialogDescription>

        {/* Header */}
        <DialogHeader className="shrink-0 bg-background border-b">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="min-w-0 flex-1">
              {appointmentData?.client?.name && (
                <div className="text-[15px] font-medium text-muted-foreground mt-0.5 truncate">
                  {t("appointment.header.client", "Client:")}{" "}{appointmentData.client.name}
                </div>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Loading/Error states */}
        {!shouldShowContent && (
          <div className="flex-1 flex items-center justify-center">
            <span className="text-muted-foreground text-sm">
              {isLoading ? t("appointment.details.loading", "Loading appointment data...") : t("appointment.details.not_selected", "No appointment selected")}
            </span>
          </div>
        )}

        {/* Accordion content */}
        {shouldShowContent && (
          <>
            {/* Update indicator */}
            {isEditing && (
              <div className="w-full bg-amber-100 text-amber-800 text-center py-2 text-xs font-medium">
                {t("common.labels.updating", "Updating...")}
              </div>
            )}

            {/* Scrollable accordion area */}
            <div className="flex-1 overflow-y-auto">
              <Accordion
                type="single"
                collapsible
                value={openValue}
                onValueChange={(v) => setOpenValue(v as SectionKey | undefined)}
              >
                {/* Детали */}
                <AccordionItem value="details">
                  <AccordionTriggerRow
                    icon={<Calendar className="h-6 w-6 text-cyan-500" strokeWidth={1} />}
                    title={t("appointment.section.details", "Appointment details")}
                    summary={summaries.details}
                  />
                  <AccordionContent className="px-4 pb-4 pt-2 bg-background border-t">
                    {openValue === "details" && <DetailsSection />}
                  </AccordionContent>
                </AccordionItem>

                {/* Статус визита */}
                <AccordionItem value="status">
                  <AccordionTriggerRow
                    icon={<CheckCircle className="h-6 w-6 text-cyan-500" strokeWidth={1} />}
                    title={t("appointment.section.visit_status", "Visit status")}
                    summary={summaries.status}
                  />
                  <AccordionContent className="px-4 pb-4 pt-2 bg-background border-t">
                    {openValue === "status" && <StatusSectionMobile />}
                  </AccordionContent>
                </AccordionItem>

                {/* Данные клиента */}
                <AccordionItem value="client-data">
                  <AccordionTriggerRow
                    icon={<User className="h-6 w-6 text-cyan-500" strokeWidth={1} />}
                    title={t("appointment.section.client_data", "Client data")}
                    summary={summaries.client}
                  />
                  <AccordionContent className="px-4 pb-4 pt-2 bg-background border-t">
                    {openValue === "client-data" && <ClientDataSection />}
                  </AccordionContent>
                </AccordionItem>

                {/* Client notes with photos and videos (boona docs/codebase/attachments/) */}
                <AccordionItem value="client-notes">
                  <AccordionTriggerRow
                    icon={<NotebookPen className="h-6 w-6 text-cyan-500" strokeWidth={1} />}
                    title={t("appointment.sidebar.client_notes", "Client notes")}
                    summary={t("attachments.notes_summary", "Notes, photos and videos")}
                  />
                  <AccordionContent className="px-4 pb-4 pt-2 bg-background border-t">
                    {openValue === "client-notes" && <ClientNotesSection compact />}
                  </AccordionContent>
                </AccordionItem>

                {/* Оплата */}
                <AccordionItem value="payment">
                  <AccordionTriggerRow
                    icon={<DollarSign className="h-6 w-6 text-cyan-500" strokeWidth={1} />}
                    title={t("appointment.section.visit_payment", "Visit payment")}
                    summary={summaries.payment}
                  />
                  <AccordionContent className="px-4 pb-4 pt-2 bg-background border-t">
                    {openValue === "payment" && <PaymentSectionMobile />}
                  </AccordionContent>
                </AccordionItem>

                {/* Consumables: only for records_consumables_edit_access (owner / admin, not "worker") */}
                {policy.records.writeOff && (
                <AccordionItem value="consumables">
                  <AccordionTriggerRow
                    icon={<Package className="h-6 w-6 text-cyan-500" strokeWidth={1} />}
                    title={t("appointment.section.consumables", "Consumables write-off")}
                    summary={summaries.consumables}
                  />
                  <AccordionContent className="px-4 pb-4 pt-2 bg-background border-t">
                    {openValue === "consumables" && <ConsumablesSection />}
                  </AccordionContent>
                </AccordionItem>
                )}

                {/* История посещений */}
                <AccordionItem value="visit-history">
                  <AccordionTriggerRow
                    icon={<History className="h-6 w-6 text-cyan-500" strokeWidth={1} />}
                    title={t("appointment.section.visit_history", "Visit history")}
                    summary={summaries.visitHistory}
                  />
                  <AccordionContent className="px-4 pb-4 pt-2 bg-background border-t">
                    {openValue === "visit-history" && <VisitHistorySectionMobile />}
                  </AccordionContent>
                </AccordionItem>

                {/* История изменений */}
                <AccordionItem value="changes">
                  <AccordionTriggerRow
                    icon={<FileText className="h-6 w-6 text-cyan-500" strokeWidth={1} />}
                    title={t("appointment.section.changes", "Changes history")}
                    summary={summaries.changes}
                  />
                  <AccordionContent className="px-4 pb-4 pt-2 bg-background border-t">
                    {openValue === "changes" && <ChangesSectionMobile />}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* Sticky footer */}
            <div className="shrink-0 flex gap-3 bg-background px-4 py-3 border-t">
              <Button
                variant="destructive"
                className="flex-1 h-12 font-medium text-[15px]"
                onClick={handleDelete}
                disabled={isEditing}
              >
                {t("common.buttons.delete", "Delete")}
              </Button>
              <Button
                className="flex-1 h-12 font-semibold text-[15px] bg-[#ff6b6b] hover:bg-[#ff5252] text-white"
                onClick={handleSave}
                disabled={isEditing || !appointment || isSplitInvalid}
              >
                {isEditing ? t("common.buttons.saving", "Saving...") : t("common.buttons.save", "Save")}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
